/**
 * Filter panel for the Delivery Lines sub-tab.
 *
 * Grouped compact layout matching ASN / SO Lines filter panels.
 * Supplier-portal label flip on upstream entity columns via props.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { X, Calendar, Search } from 'lucide-react';
import SearchableSelect from '../../ui/SearchableSelect';

interface FilterOption {
  value: string;
  label: string;
}

export interface DeliveryLinesFilters {
  poNumber: string;
  partNumber: string;
  supplier: string;
  customer: string;
  customerGroup: string;
  commodityGroup: string;
  commodityType: string;
  dateFrom: string;
  dateTo: string;
}

interface DeliveryLinesFilterProps {
  filters: DeliveryLinesFilters;
  supplierOptions: FilterOption[];
  customerOptions: FilterOption[];
  customerGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  /** On-screen label for the supplier/site column (flips in supplier-portal mode). */
  supplierLabel: string;
  /** On-screen label for the counterparty (customer) column. */
  customerLabel: string;
  /** On-screen label for the counterparty group column. */
  customerGroupLabel: string;
  onFilterChange: (key: keyof DeliveryLinesFilters, value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
}

export function DeliveryLinesFilter({
  filters,
  supplierOptions,
  customerOptions,
  customerGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  supplierLabel,
  customerLabel,
  customerGroupLabel,
  onFilterChange,
  onClearFilters,
  onSearch,
}: DeliveryLinesFilterProps) {
  const inputCls = 'w-full h-8 px-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const dateInputCls = 'w-full h-8 pl-7 pr-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const labelCls = 'text-xs font-medium mb-1 block text-foreground leading-tight';
  const sectionTitleCls = 'text-[11px] font-semibold uppercase tracking-wide text-foreground mb-1.5';
  const sectionCls = 'rounded-md border border-border/50 bg-muted/20 px-2.5 py-2';
  const compactGridCls = 'grid grid-cols-2 gap-x-2 gap-y-1.5';
  const selectCompactCls = '[&>button]:h-8 [&>button]:min-h-8 [&>button]:py-1 [&>button]:px-2 [&>button]:text-sm';

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-4 border-b border-border/60">
        <CardTitle className="text-sm font-semibold">Filter Deliveries</CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-3 space-y-2">
        <div className={sectionCls}>
          <div className={sectionTitleCls}>Customer &amp; Site</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>{supplierLabel}</label>
              <SearchableSelect
                options={supplierOptions}
                value={filters.supplier}
                onChange={(val) => onFilterChange('supplier', val)}
                placeholder={`All ${supplierLabel}s`}
                className={selectCompactCls}
              />
            </div>
            <div>
              <label className={labelCls}>{customerGroupLabel}</label>
              <SearchableSelect
                options={customerGroupOptions}
                value={filters.customerGroup}
                onChange={(val) => onFilterChange('customerGroup', val)}
                placeholder={`All ${customerGroupLabel}s`}
                className={selectCompactCls}
              />
            </div>
            <div>
              <label className={labelCls}>{customerLabel}</label>
              <SearchableSelect
                options={customerOptions}
                value={filters.customer}
                onChange={(val) => onFilterChange('customer', val)}
                placeholder={`All ${customerLabel}s`}
                className={selectCompactCls}
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-2">
          <div className={sectionCls}>
            <div className={sectionTitleCls}>Part &amp; Commodity</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1.5">
              <div>
                <label className={labelCls}>Commodity Group</label>
                <select
                  value={filters.commodityGroup}
                  onChange={(e) => onFilterChange('commodityGroup', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  {commodityGroupOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Commodity Type</label>
                <select
                  value={filters.commodityType}
                  onChange={(e) => onFilterChange('commodityType', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  {commodityTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Part Number</label>
                <input
                  type="text"
                  placeholder="Part number..."
                  value={filters.partNumber}
                  onChange={(e) => onFilterChange('partNumber', e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <div className={sectionCls}>
            <div className={sectionTitleCls}>Dates</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>Date From</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                    className={dateInputCls}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Date To</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => onFilterChange('dateTo', e.target.value)}
                    className={dateInputCls}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={sectionCls}>
          <div className={sectionTitleCls}>Order</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>SO Number</label>
              <input
                type="text"
                placeholder="SO Number..."
                value={filters.poNumber}
                onChange={(e) => onFilterChange('poNumber', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
          <Button type="button" size="sm" className="px-5" onClick={onSearch}>
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onClearFilters} className="px-5">
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear All
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
